import { ThemeOptions } from "@mui/material/styles";

import { Brand } from "merchant-portal-frontend/src/config";
import breadLoginBackgroundImage from "merchant-portal-frontend/src/images/login/breadLoginBackgroundImage.png";

import { breadBranding } from "./branding.config";
import {
	black,
	breadBlack,
	breadCerulean,
	breadDarkOcean,
	breadError,
	breadErrorDark,
	breadErrorLight,
	breadFocusBlue,
	breadGray,
	breadInfo,
	breadInfoDark,
	breadInfoLight,
	breadLightGray,
	breadLineGray,
	breadMustard,
	breadOffWhite,
	breadPrimary,
	breadPrimary04,
	breadPrimary16,
	breadPrimaryDark,
	breadPrimaryEvenMoreMediumLight,
	breadPrimaryLight,
	breadPrimaryMediumLight,
	breadPrimaryVeryLight,
	breadQuince,
	breadSeaFoam,
	breadSecondary,
	breadSecondaryDark,
	breadSecondaryLight,
	breadSlightlyDarkerSeaFoam,
	breadSuccess,
	breadSuccessDark,
	breadSuccessLight,
	breadWarning,
	breadWarningDark,
	breadWarningLight,
	breadWarningVeryLight,
	breadWine,
	shadowColor,
	white,
} from "./colors";

const breadTheme: ThemeOptions = {
	themeBrand: Brand.Bread,
	loginBackgroundImage: breadLoginBackgroundImage,
	primary1: breadPrimary,
	primary2: breadSecondary,
	secondary1: breadSeaFoam,
	secondary2: breadWine,
	secondary3: breadQuince,
	tertiary2: breadMustard,
	white,
	black,
	layout: {
		blueGray: breadLineGray,
		gray: breadGray,
		extralightGray: breadOffWhite,
	},
	text: {
		bodyAndIcons: breadBlack,
		activeText: breadSecondary,
		hoverText: breadPrimary,
		disabledText: breadLineGray,
		hintText: breadLightGray,
		leftNavIconAndText: breadGray,
		logoText: breadBlack,
	},
	structure: {
		hoverBG: breadPrimaryVeryLight,
		accent: breadPrimaryEvenMoreMediumLight,
		calendarLeftBG: breadPrimaryVeryLight,
		skeleton: breadPrimaryVeryLight,
		cardStroke: breadPrimary,
		shadow: `drop-shadow(0px 2px 3px ${shadowColor})`,
	},
	formElements: {
		formElementBorder: breadLineGray,
		dropdownOpenBG: breadOffWhite,
		calendarSelectionBG: breadPrimary,
		requirementIconOutline: breadLightGray,
		requirementIconBGValid: breadPrimary,
		dropdownIconFill: breadGray,
	},
	buttons: {
		loginBase: breadPrimary,
		loginText: white,
		base: breadPrimary,
		hover: breadPrimary,
		focus: breadPrimary,
		dangerHover: breadQuince,
		dangerFocus: breadQuince,
	},
	kpis: {
		gradients: {
			pending: breadWine,
			authorized: breadCerulean,
			settled: breadSuccess,
		},
		text: {
			pillText: white,
			defaultText: breadBlack,
		},
	},
	transactions: {
		authorizedBG: breadCerulean,
		pendingBG: breadWine,
		settledBG: breadSuccess,
		cancelledBG: breadDarkOcean,
		refundedBG: breadPrimary,
		expiredBG: breadGray,
		chargedbackBG: breadSlightlyDarkerSeaFoam,
		text: {
			authorized: breadCerulean,
			pending: breadWine,
			settled: breadSuccess,
			cancelled: breadDarkOcean,
			refunded: breadPrimary,
			expired: breadGray,
		},
	},
	icons: {
		primary: breadPrimary,
		secondary: breadPrimary,
		outline: breadPrimary,
	},
	...breadBranding,
	palette: {
		primary: {
			main: breadPrimary,
			light: breadPrimaryLight,
			dark: breadPrimaryDark,
			contrastText: white,
			veryLight: breadPrimaryVeryLight,
		},
		secondary: {
			main: breadSecondary,
			light: breadSecondaryLight,
			dark: breadSecondaryDark,
			contrastText: white,
		},
		error: {
			main: breadError,
			light: breadErrorLight,
			dark: breadErrorDark,
			contrastText: white,
		},
		warning: {
			main: breadWarning,
			light: breadWarningLight,
			veryLight: breadWarningVeryLight,
			dark: breadWarningDark,
			contrastText: breadBlack,
		},
		info: {
			main: breadInfo,
			light: breadInfoLight,
			dark: breadInfoDark,
			contrastText: breadBlack,
		},
		success: {
			main: breadSuccess,
			light: breadSuccessLight,
			dark: breadSuccessDark,
			contrastText: breadBlack,
		},
		common: {
			lineGray: breadLineGray,
			darkGray: breadBlack,
			gray: breadGray,
			black,
			white,
		},
	},
	components: {
		MuiButton: {
			styleOverrides: {
				root: {
					"minWidth": 0,
					"&.Mui-focusVisible": {
						border: `2px solid ${breadFocusBlue}`,
					},
				},
				outlined: {
					"border": `1px solid ${breadPrimary}`,
					":hover": {
						border: `2px solid ${breadPrimary}`,
						padding: "1.1rem 3.9rem",
					},
					":active": {
						border: `2px solid ${breadSecondary}`,
						color: `${breadSecondary}`,
					},
					"&.Mui-focusVisible": {
						border: `2px solid ${breadFocusBlue}`,
					},
				},
				contained: {
					":hover": {
						backgroundColor: breadPrimaryLight,
					},
					":active": {
						backgroundColor: breadSecondary,
					},
					":disabled": {
						backgroundColor: breadPrimaryMediumLight,
						color: white,
					},
					"&.Mui-focusVisible": {
						border: `2px solid ${breadFocusBlue}`,
					},
				},
				text: {
					"borderBottom": `1px solid ${breadPrimaryLight}`,
					":disabled": {
						color: breadPrimaryMediumLight,
						borderBottom: `2px solid ${breadPrimaryMediumLight}`,
					},
					":active": {
						color: breadSecondary,
					},
					":hover": {
						borderBottom: `2px solid ${breadPrimary}`,
					},
					"&.Mui-focusVisible": {
						border: `2px solid ${breadFocusBlue}`,
					},
				},
			},
		},
		MuiInputBase: {
			styleOverrides: {
				root: {
					color: breadBlack,
				},
			},
		},
		MuiTooltip: {
			styleOverrides: {
				tooltip: {
					color: breadBlack,
					backgroundColor: white,
					border: `1px solid ${breadLineGray}`,
				},
				arrow: {
					color: breadLineGray,
				},
			},
		},
		MuiIconButton: {
			styleOverrides: {
				root: {
					"color": breadPrimary,
					"&.Mui-disabled": {
						border: `1px solid ${breadFocusBlue}`,
						color: breadPrimary,
					},
					":hover": {
						backgroundColor: breadPrimary04,
					},
					":active": {
						backgroundColor: breadPrimary16,
					},
				},
			},
		},
		MuiFormLabel: {
			styleOverrides: {
				root: {
					"color": breadGray,
					"&.Mui-focused": {
						color: breadGray,
					},
				},
			},
		},
		MuiFormControlLabel: {
			styleOverrides: {
				label: {
					"color": breadBlack,
					"&.Mui-disabled": {
						color: breadLineGray,
					},
				},
			},
		},
		MuiDataGrid: {
			styleOverrides: {
				root: {
					color: breadBlack,
				},
				columnHeaders: {
					backgroundColor: breadPrimaryVeryLight,
				},
			},
		},
		MuiSkeleton: {
			styleOverrides: {
				root: {
					backgroundColor: breadPrimaryVeryLight,
				},
			},
		},
		MuiOutlinedInput: {
			styleOverrides: {
				root: {
					"& .MuiOutlinedInput-notchedOutline": {
						border: `1px solid ${breadLineGray}`,
					},
					"&.Mui-disabled:hover .MuiOutlinedInput-notchedOutline": {
						border: `1px solid ${black}`,
					},
				},
			},
		},
		MuiAppBar: {
			styleOverrides: {
				root: {
					borderBottom: `1px solid ${breadLineGray}`,
					color: breadBlack,
				},
			},
		},
		MuiAccordionSummary: {
			styleOverrides: {
				root: {
					"& .MuiAccordionSummary-expandIconWrapper": {
						"color": breadPrimary,
						"&.Mui-disabled": {
							border: `2px solid ${breadFocusBlue}`,
							color: breadPrimary,
							padding: "0.6rem",
						},
						":hover": {
							backgroundColor: breadPrimary04,
						},
						":active": {
							backgroundColor: breadPrimary16,
						},
					},
				},
			},
		},
		MuiTableHead: {
			styleOverrides: {
				root: {
					backgroundColor: breadPrimaryVeryLight,
					fontWeight: 500,
					top: 0,
					zIndex: 1,
					position: "sticky",
				},
			},
		},
		MuiTableRow: {
			styleOverrides: {
				root: {
					"& td:first-of-type": {
						borderLeftWidth: 1,
						borderLeftColor: breadLineGray,
						borderLeftStyle: "solid",
					},
					"& td:last-of-type": {
						borderRightWidth: 1,
						borderRightColor: breadLineGray,
						borderRightStyle: "solid",
					},
				},
			},
		},
		MuiTableCell: {
			styleOverrides: {
				body: {
					borderBottomWidth: 1,
					borderBottomColor: breadLineGray,
					borderBottomStyle: "solid",
				},
				head: {
					"borderTopWidth": 1,
					"borderTopColor": breadLineGray,
					"borderTopStyle": "solid",
					"borderBottomWidth": 1,
					"borderBottomColor": breadLineGray,
					"borderBottomStyle": "solid",
					"&:first-of-type": {
						borderLeftWidth: 1,
						borderLeftColor: breadLineGray,
						borderLeftStyle: "solid",
					},
					"&:last-of-type": {
						borderRightWidth: 1,
						borderRightColor: breadLineGray,
						borderRightStyle: "solid",
					},
				},
			},
		},
		MuiToggleButtonGroup: {
			styleOverrides: {
				root: {
					"backgroundColor": breadPrimaryVeryLight,
					"&.Mui-disabled": {
						backgroundColor: white,
						color: breadLightGray,
					},
				},
			},
		},
		MuiToggleButton: {
			styleOverrides: {
				root: {
					"color": breadGray,
					"&:hover": {
						backgroundColor: breadPrimaryVeryLight,
						color: breadPrimary,
					},
					"&.Mui-selected": {
						"backgroundColor": breadPrimary,
						"color": white,
						"&:hover": {
							backgroundColor: breadPrimary,
							color: white,
						},
					},
				},
			},
		},
		MuiTab: {
			styleOverrides: {
				root: {
					"&:hover": {
						backgroundColor: breadPrimaryVeryLight,
						borderBottom: `3px solid ${breadSeaFoam}`,
						color: breadPrimary,
					},
					"&:active": {
						backgroundColor: breadPrimaryEvenMoreMediumLight,
						borderBottom: `3px solid ${breadPrimary}`,
						color: breadPrimary,
					},
				},
			},
		},
	},
};

export { breadTheme };
